
import './index.css';
import React from 'react';
import ReactDOM from 'react-dom/client';
import { QueryClientProvider } from '@tanstack/react-query';
import App from './App';
import { JobManagerProvider } from './contexts/JobManagerContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import LoginPage from './components/LoginPage';
import CommercialRouter from './components/commercial/router/CommercialRouter';
import { isCommercial } from './config/appMode';
import { queryClient } from './app/queryClient';
import { ToastProvider } from './components/shared/Toast';
import { AppErrorBoundary } from './components/shared/AppErrorBoundary';
import { installGlobalErrorHandlers } from './services/errorReporter';
import { initAnalytics } from './services/analytics';

// Internal-only auth guard (commercial uses router-based RequireAuth)
const InternalAuthGuard: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isLoading } = useAuth();
  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <svg className="w-8 h-8 text-indigo-500 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          <span className="text-slate-400 text-sm font-medium">Đang tải...</span>
        </div>
      </div>
    );
  }
  if (!user) return <LoginPage />;
  return <>{children}</>;
};

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error("Could not find root element to mount to");
}

installGlobalErrorHandlers();
initAnalytics();

const root = ReactDOM.createRoot(rootElement);
root.render(
  <React.StrictMode>
    <AppErrorBoundary>
      <AuthProvider>
        <QueryClientProvider client={queryClient}>
          {isCommercial ? (
            <JobManagerProvider>
              <ToastProvider>
                <CommercialRouter />
              </ToastProvider>
            </JobManagerProvider>
          ) : (
            <InternalAuthGuard>
              <JobManagerProvider>
                <ToastProvider>
                  <App />
                </ToastProvider>
              </JobManagerProvider>
            </InternalAuthGuard>
          )}
        </QueryClientProvider>
      </AuthProvider>
    </AppErrorBoundary>
  </React.StrictMode>
);
